import { useEffect, useRef, useCallback, useState } from 'react';
import type * as monacoEditor from 'monaco-editor/esm/vs/editor/editor.api';
import type { UploadingAndUploadedFile } from 'src/utils/useProject/useProject';
import type { CompileFunction, Language } from 'src/compile';

const MCP_WS_URL = 'ws://127.0.0.1:19475';
const RECONNECT_DELAY_MS = 3000;

/** Simple string hash for optimistic concurrency (djb2 algorithm) */
function contentHash(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

export type McpBridgeStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

interface McpBridgeOptions {
  enabled: boolean;
  orgSlug: string;
  projectSlug: string;
  editorRef: React.MutableRefObject<monacoEditor.editor.IStandaloneCodeEditor>;
  src: string;
  setSrc: (src: string) => void;
  compileFn: CompileFunction;
  files: UploadingAndUploadedFile[];
  language: Language;
  shouldMinify: boolean;
  updateProjectSrc: (src: string) => Promise<string | undefined | void>;
  fetchRevisionHistory: (limit: number) => Promise<unknown>;
}

interface ToolCallMessage {
  type: 'tool_call';
  id: string;
  tool: string;
  params: Record<string, unknown>;
}

export function useMcpBridge(options: McpBridgeOptions): McpBridgeStatus {
  const {
    enabled,
    orgSlug,
    projectSlug,
    editorRef,
    src,
    setSrc,
    compileFn,
    files,
    language,
    shouldMinify,
    updateProjectSrc,
    fetchRevisionHistory,
  } = options;

  const [status, setStatus] = useState<McpBridgeStatus>('disconnected');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep refs to current values so tool handlers always read the latest
  const srcRef = useRef(src);
  srcRef.current = src;
  const filesRef = useRef(files);
  filesRef.current = files;
  const languageRef = useRef(language);
  languageRef.current = language;
  const shouldMinifyRef = useRef(shouldMinify);
  shouldMinifyRef.current = shouldMinify;
  const compileFnRef = useRef(compileFn);
  compileFnRef.current = compileFn;
  const setSrcRef = useRef(setSrc);
  setSrcRef.current = setSrc;
  const updateProjectSrcRef = useRef(updateProjectSrc);
  updateProjectSrcRef.current = updateProjectSrc;
  const fetchRevisionHistoryRef = useRef(fetchRevisionHistory);
  fetchRevisionHistoryRef.current = fetchRevisionHistory;

  const handleToolCall = useCallback(
    async (msg: ToolCallMessage): Promise<void> => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

      const sendResult = (success: boolean, data: unknown) => {
        const currentWs = wsRef.current;
        if (!currentWs || currentWs.readyState !== WebSocket.OPEN) return;
        currentWs.send(
          JSON.stringify(
            success
              ? { type: 'tool_result', id: msg.id, success: true, result: data }
              : {
                  type: 'tool_result',
                  id: msg.id,
                  success: false,
                  error: data,
                },
          ),
        );
      };

      try {
        switch (msg.tool) {
          case 'read_source': {
            const source = srcRef.current;
            const lines = source.split('\n');
            const start =
              (msg.params.startLine as number | undefined) ?? 1;
            const end =
              (msg.params.endLine as number | undefined) ?? lines.length;
            const slice = lines.slice(start - 1, end);
            const numbered = slice
              .map((line, i) => `${start + i}: ${line}`)
              .join('\n');
            sendResult(true, {
              content: numbered,
              totalLines: lines.length,
              contentHash: contentHash(source),
            });
            break;
          }

          case 'search_source': {
            const source = srcRef.current;
            const lines = source.split('\n');
            const pattern = new RegExp(
              msg.params.pattern as string,
              'gi',
            );
            const contextLines =
              (msg.params.contextLines as number | undefined) ?? 2;
            const matches: Array<{
              line: number;
              text: string;
              context: string[];
            }> = [];

            for (let i = 0; i < lines.length; i++) {
              if (pattern.test(lines[i])) {
                const ctxStart = Math.max(0, i - contextLines);
                const ctxEnd = Math.min(
                  lines.length - 1,
                  i + contextLines,
                );
                const context = lines
                  .slice(ctxStart, ctxEnd + 1)
                  .map((l, j) => `${ctxStart + j + 1}: ${l}`);
                matches.push({ line: i + 1, text: lines[i], context });
              }
              pattern.lastIndex = 0;
            }

            sendResult(true, {
              matches,
              totalMatches: matches.length,
              contentHash: contentHash(source),
            });
            break;
          }

          case 'read_output_html': {
            const source = srcRef.current;
            const result = await compileFnRef.current({
              src: source,
              language: languageRef.current,
              files: filesRef.current,
              shouldMinify: shouldMinifyRef.current,
              remoteImages: true,
              isForRenderedPreview: false,
            });
            const html = result.html;
            const htmlLines = html.split('\n');
            const start =
              (msg.params.startLine as number | undefined) ?? 1;
            const end =
              (msg.params.endLine as number | undefined) ??
              htmlLines.length;
            const slice = htmlLines
              .slice(start - 1, end)
              .join('\n');
            sendResult(true, {
              content: slice,
              totalLines: htmlLines.length,
            });
            break;
          }

          case 'get_compilation_errors': {
            const source = srcRef.current;
            try {
              const result = await compileFnRef.current({
                src: source,
                language: languageRef.current,
                files: filesRef.current,
                shouldMinify: false,
                remoteImages: false,
                isForRenderedPreview: false,
              });
              sendResult(true, {
                errors: result.errors ?? [],
                hasErrors: (result.errors?.length ?? 0) > 0,
              });
            } catch (e) {
              sendResult(true, {
                errors: [
                  {
                    type: 'fatal',
                    message:
                      e instanceof Error ? e.message : String(e),
                  },
                ],
                hasErrors: true,
              });
            }
            break;
          }

          case 'edit_source': {
            const editor = editorRef.current;
            if (!editor) {
              sendResult(false, 'Editor not available');
              break;
            }
            const model = editor.getModel();
            if (!model) {
              sendResult(false, 'Editor model not available');
              break;
            }

            // Optimistic concurrency: reject if source changed since last read
            const expectedHash = msg.params.contentHash as string | undefined;
            if (expectedHash && contentHash(srcRef.current) !== expectedHash) {
              sendResult(false, 'Source has changed since last read. Re-read with read_source and retry.');
              break;
            }

            const newContent = msg.params.newContent as string;
            const startLine = msg.params.startLine as
              | number
              | undefined;
            const endLine = msg.params.endLine as number | undefined;

            let range: monacoEditor.IRange;
            if (startLine === undefined && endLine === undefined) {
              range = model.getFullModelRange();
            } else {
              const sl = startLine ?? 1;
              const el = endLine ?? model.getLineCount();
              range = {
                startLineNumber: sl,
                startColumn: 1,
                endLineNumber: el,
                endColumn: model.getLineMaxColumn(el),
              };
            }

            editor.executeEdits('mcp-bridge', [
              { range, text: newContent },
            ]);

            const updatedSrc = model.getValue();
            setSrcRef.current(updatedSrc);

            sendResult(true, {
              success: true,
              totalLines: model.getLineCount(),
            });
            break;
          }

          case 'list_assets': {
            sendResult(true, {
              assets: filesRef.current
                .filter(
                  (a): a is typeof a & { remoteUrl: string } =>
                    a.remoteUrl !== undefined,
                )
                .map((a) => ({
                  relativePath: a.relativePath,
                  remoteUrl: a.remoteUrl,
                })),
            });
            break;
          }

          case 'trigger_save': {
            const currentSrc = srcRef.current;
            await updateProjectSrcRef.current(currentSrc);
            sendResult(true, { saved: true });
            break;
          }

          case 'trigger_export': {
            const source = srcRef.current;
            const result = await compileFnRef.current({
              src: source,
              language: languageRef.current,
              files: filesRef.current,
              shouldMinify: shouldMinifyRef.current,
              remoteImages: false,
              isForRenderedPreview: false,
            });
            sendResult(true, { html: result.html });
            break;
          }

          case 'get_revision_history': {
            const limit = (msg.params.limit as number | undefined) ?? 10;
            const revisions = await fetchRevisionHistoryRef.current(limit);
            sendResult(true, { revisions });
            break;
          }

          default:
            sendResult(
              false,
              `Tool '${msg.tool}' is not yet implemented on the browser side.`,
            );
        }
      } catch (err) {
        sendResult(
          false,
          err instanceof Error ? err.message : String(err),
        );
      }
    },
    [editorRef],
  );

  useEffect(() => {
    if (!enabled) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setStatus('disconnected');
      return;
    }

    let disposed = false;

    function connect() {
      if (disposed) return;
      setStatus((prev) =>
        prev === 'disconnected' ? 'connecting' : 'reconnecting',
      );

      const ws = new WebSocket(MCP_WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        if (disposed) {
          ws.close();
          return;
        }
        setStatus('connected');
        ws.send(
          JSON.stringify({ type: 'register', orgSlug, projectSlug }),
        );
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data as string);
          if (msg.type === 'tool_call') {
            handleToolCall(msg);
          } else if (msg.type === 'ping') {
            ws.send(JSON.stringify({ type: 'pong' }));
          }
        } catch (err) {
          console.error('[mcp-bridge] failed to parse message:', err);
        }
      };

      ws.onclose = () => {
        if (disposed) return;
        wsRef.current = null;
        setStatus('reconnecting');
        reconnectTimerRef.current = setTimeout(
          connect,
          RECONNECT_DELAY_MS,
        );
      };

      ws.onerror = () => {
        // onclose will fire after onerror, which handles reconnection
      };
    }

    connect();

    return () => {
      disposed = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setStatus('disconnected');
    };
  }, [enabled, orgSlug, projectSlug, handleToolCall]);

  return status;
}
